import { FC, useCallback } from "react";
import { NavLink } from "react-router-dom";
import {
  Box,
  Link as ChakraLink,
  LinkProps,
  Text,
  useBoolean,
} from "@chakra-ui/react";
import { theme } from "@ovou-inc/ovou-ui";

import { IMenuItem } from "../../types/IMenuItem";

import { RestirectedModal } from "components";

const activeStyle = {
  bg: theme.palette.ui.greys.grey5,
  textColor: theme.palette.brand.primary.black,
};

const MenuItem: FC<IMenuItem & LinkProps> = ({
  title,
  icon,
  to,
  isExternal = false,
  onTrial = false,
  close,
  canEdit = true,
  restrictedMessage,
  onClick,
  ...props
}) => {
  const [
    isRestrictedModalOpen,
    { on: openRestrictedModal, off: closeRestrictedModal },
  ] = useBoolean();

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      if (restrictedMessage && !canEdit) {
        openRestrictedModal();
        return;
      }
      if (onClick) {
        await onClick(e);
        return;
      }

      close?.();
    },
    [onClick, restrictedMessage, canEdit, close, openRestrictedModal],
  );

  const shouldApplyActiveLink = to && to !== "" && !onClick;

  return (
    <>
      <ChakraLink
        display="flex"
        alignItems="center"
        borderRadius="lg"
        cursor="pointer"
        p={2}
        textColor={theme.palette.ui.greys.grey1}
        fontSize="lg"
        fontWeight={600}
        transition="all 150ms ease-in-out"
        textDecoration="none"
        _hover={activeStyle}
        {...(shouldApplyActiveLink && { _activeLink: activeStyle })}
        onClick={handleClick}
        {...props}
        {...(isExternal
          ? { href: to, isExternal }
          : to && to !== ""
            ? { to, as: NavLink as any }
            : {})}
      >
        {icon}
        <Text marginX={3}>{title}</Text>
        {onTrial && <TrialTag />}
      </ChakraLink>
      <RestirectedModal
        isOpen={isRestrictedModalOpen}
        onClose={closeRestrictedModal}
        message={restrictedMessage}
      />
    </>
  );
};

const TrialTag: FC = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    width={10}
    height={5}
    paddingX={1}
    borderRadius="base"
    backgroundColor={theme.palette.brand.primary.black}
  >
    <Text
      fontSize="2xs"
      lineHeight="taller"
      fontWeight="semibold"
      textTransform="uppercase"
      color={theme.palette.brand.primary.white}
    >
      TRIAL
    </Text>
  </Box>
);

export default MenuItem;
